/* Copyright (c) 2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useState, useEffect } from "react";
import { isNil } from "~/tools";
import { useElementNavigation } from "./useElementNavigation";

export function useActionTabNavigation(tabStepMapping: Record<string, number>) {
  const { currentHash, updateHash } = useElementNavigation();
  const [currentStep, setCurrentStep] = useState(0);

  const handleTab = (step: number) => {
    const actionTabKey =
      Object.keys(tabStepMapping).find((key) => tabStepMapping[key] === step) ??
      Object.keys(tabStepMapping)[0];
    updateHash(
      { actionTab: step === 0 ? null : actionTabKey }, // Remove hash when on the first tab
      { merge: true }
    );
    setCurrentStep(step);
  };

  useEffect(() => {
    const actionTabKey = currentHash?.actionTab;
    // If a non-details top-level tab is selected or action tab is invalid, reset action tab to default
    if (
      (currentHash?.tab && currentHash.tab !== "details") ||
      isNil(actionTabKey) ||
      !(actionTabKey in tabStepMapping)
    ) {
      updateHash({ actionTab: null }, { merge: true });
      setCurrentStep(0);
      return;
    }

    const actionStep = tabStepMapping[actionTabKey];
    if (actionStep !== currentStep) {
      setCurrentStep(actionStep);
    }
  }, [currentHash, updateHash, currentStep, tabStepMapping]);

  return { currentStep, handleTab };
}
